import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One script's visual direction, and — for AI Video — its actual video prompts.
 *
 * THE FOUNDER, 2026-09-09: "Creative elements still contains nothing regarding
 * the video direction/prompt... For AI Video creative types specifically, this
 * step should be producing or showing the actual visual direction / prompt that
 * feeds the video generation — right now there's nothing there related to that
 * at all."
 *
 * ROOT CAUSE, and it is not that the content is missing. Two separate things
 * exist and neither reached this screen:
 *
 *   1. **The visual direction** has been written on every section of every
 *      script since the scriptwriter was built (`{section, timing, spoken_text,
 *      visual_direction}`). Step 4 shows it; step 6 — the step that submits the
 *      script to be BUILT — never did.
 *   2. **The video prompts** are built inside the submit and stored on the
 *      production as `ai_prompt_set`. Verified live: **2 of 180 productions
 *      carry one, and nothing read the field except the Trello card builder.**
 *      No endpoint returned it; no screen drew it. So the operator pressed
 *      Submit having never seen the prompts, and any warning on them was
 *      invisible — including, on the Founder's own `prod_7ae1de723ed5`, "the
 *      middle of this ad reached no clip prompt".
 *
 * THE PREVIEW IS A DELIBERATE ACT. It costs a text-model call, so it happens on
 * a button press and never on load, and it writes nothing. No video model is
 * reachable from this path.
 */
export function useVideoDirection({ scriptId, productionId }) {
  const [direction, setDirection] = useState(null);
  const [state, setState] = useState('idle');        // idle | loading | ok | failed
  const [error, setError] = useState(null);

  // The prompt set, from whichever of the two sources applies. `source` says
  // which, because "what was actually sent to production" and "what a preview
  // would build now" are different claims and must not look alike.
  const [prompts, setPrompts] = useState(null);
  const [promptState, setPromptState] = useState('idle');
  const [promptError, setPromptError] = useState(null);
  const [source, setSource] = useState(null);        // stored | preview

  const load = useCallback(async () => {
    if (!scriptId) return;
    setState('loading');
    try {
      const res = await axios.get(`${API}/scripts/${scriptId}/visual-direction`);
      setDirection(res.data);
      setState('ok');
      setError(null);
    } catch (e) {
      // A read that failed is not a script with no direction.
      setState('failed');
      setError(errorText(e));
    }
  }, [scriptId]);

  useEffect(() => { load(); }, [load]);

  /** What was actually put in front of a human, for a submitted script. */
  const loadStored = useCallback(async () => {
    if (!productionId) return;
    setPromptState('loading');
    try {
      const res = await axios.get(`${API}/productions/${productionId}/ai-prompt-set`);
      setPrompts(res.data);
      setSource('stored');
      setPromptState('ok');
      setPromptError(null);
    } catch (e) {
      setPromptState('failed');
      setPromptError(errorText(e));
    }
  }, [productionId]);

  /** What the prompts WOULD be. Builds nothing permanent, submits nothing. */
  const preview = useCallback(async () => {
    if (!scriptId) return;
    setPromptState('loading');
    try {
      const res = await axios.post(
        `${API}/scripts/${scriptId}/ai-video-prompts/preview`);
      setPrompts(res.data);
      setSource('preview');
      setPromptState('ok');
      setPromptError(null);
    } catch (e) {
      setPromptState('failed');
      setPromptError(errorText(e));
    }
  }, [scriptId]);

  /**
   * Replace ONE clip's prompt with the operator's own words. No model.
   *
   * THE FOUNDER, 2026-09-10: "I do want to be able to edit the text in the
   * prompt or provide refinements as well." Same two gestures the hooks have.
   *
   * The draft the preview saved is what these change, and it is what the submit
   * now uses — before this, the preview and the submit each built their own
   * prompts, so an edit would have been made to something already discarded.
   */
  const editClip = useCallback(async (segmentIndex, text) => {
    if (!scriptId) return false;
    setPromptError(null);
    try {
      const res = await axios.patch(
        `${API}/scripts/${scriptId}/ai-video-prompts/${segmentIndex}`,
        { prompt: text });
      setPrompts((prev) => ({ ...(prev || {}), ...res.data.draft }));
      setSource('draft');
      return true;
    } catch (e) {
      // The server refuses an emptied prompt and a stale draft with different
      // reasons; both have to reach the screen intact.
      setPromptError(errorText(e));
      return false;
    }
  }, [scriptId]);

  /** Have the Specialist rewrite ONE clip from a note. */
  const refineClip = useCallback(async (segmentIndex, note) => {
    if (!scriptId) return false;
    setPromptState('loading');
    setPromptError(null);
    try {
      const res = await axios.post(
        `${API}/scripts/${scriptId}/ai-video-prompts/${segmentIndex}/refine`,
        { feedback: note });
      setPrompts((prev) => ({ ...(prev || {}), ...res.data.draft }));
      setSource('draft');
      setPromptState('ok');
      return true;
    } catch (e) {
      setPromptState('ok');
      setPromptError(errorText(e));
      return false;
    }
  }, [scriptId]);

  /** Prompts already written for this script, without paying for a new run. */
  const loadDraft = useCallback(async () => {
    if (!scriptId) return;
    setPromptState('loading');
    try {
      const res = await axios.get(`${API}/scripts/${scriptId}/ai-video-prompts`);
      if (!res.data?.has_draft) {
        setPromptState('idle');
        return;
      }
      setPrompts(res.data);
      setSource(res.data.stale ? 'stale' : 'draft');
      setPromptState('ok');
      setPromptError(null);
    } catch (e) {
      setPromptState('failed');
      setPromptError(errorText(e));
    }
  }, [scriptId]);

  return {
    direction, state, error, retry: load,
    prompts, promptState, promptError, source,
    preview, loadStored, loadDraft, editClip, refineClip,
  };
}
